import { ContactMailRounded, ExitToApp, KeyboardDoubleArrowDownRounded, KeyboardDoubleArrowUpRounded } from "@mui/icons-material";
import { Box, Collapse, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStickyBottom from "../../utils/hooks/useStickyBottom";
import { ITheme } from "../../utils/theme";
import { COMPLEX_NAV_TITLES, INavDrawerItem, TelephoneBoards, navigation } from "./NavDrawerModels";
import { MenuBoxStyle, MenuListItemStyle, MenuStyle, MenuSubFooterStyle, MenuSubHeaderItemButtonStyle, MenuSubHeaderStyle } from "./styled";

export interface NavDrawerProps {
  open: boolean,
  signedIn: boolean,
  nav: string,
  onNavTo: (to: string) => void,
  onClose: () => void,
  onSignOut: () => void,
  isTelephone: boolean,
  lastLoginTime?: string,
  isIVR: boolean,
  isDOAAdmin?: boolean,
  board: string,
  isLinesComplementBoard: boolean;
}

export const NavDrawer = ({
  open,
  signedIn,
  nav,
  onNavTo,
  onClose,
  onSignOut,
  isTelephone,
  lastLoginTime,
  isIVR,
  isDOAAdmin = false,
  board,
  isLinesComplementBoard
}: NavDrawerProps) => {
  const navigate = useNavigate();
  const smbAuthenticated = signedIn && !isIVR;
  const boards = board ? board.split(',') : [];
  const onFABoard = boards.includes(TelephoneBoards["First Aid"]);
  const onSQCBoard = boards.includes(TelephoneBoards.Squamish);
  const onDCBoard = boards.includes(TelephoneBoards["Dock Checkers"]);
  const onTB = onFABoard || onSQCBoard || onDCBoard;
  const isLines = (isLinesComplementBoard && !boards.includes("LIN") && boards.length > 0) || (isLinesComplementBoard && boards.includes("LIN") && boards.length > 1);

  const filterNavigation = (
    items: INavDrawerItem[],
    smbAuthenticated: boolean,
    isDOAAdmin: boolean,
    onFABoard: boolean,
    onSQCBoard: boolean,
    onDCBoard: boolean,
    board: string | null
  ): INavDrawerItem[] => (
    items.filter((item) => shouldShowItem(item, smbAuthenticated, isDOAAdmin, onFABoard, onSQCBoard, onDCBoard, board))
  );

  // eslint-disable-next-line complexity
  const shouldShowItem = (
    item: INavDrawerItem,
    smbAuthenticated: boolean,
    isDOAAdmin: boolean,
    onFABoard: boolean,
    onSQCBoard: boolean,
    onDCBoard: boolean,
    board: string | null
  ): boolean => {
    const {
      name,
      text,
      hideWhenNotSignedIn,
      hideWhenNotTelephone,
      hideWhenNotDOAAdmin,
      hideWhenNonVancouverUser,
      hideWhenNotLines,
      children
    } = item;

    if (!smbAuthenticated && hideWhenNotSignedIn) {
      return !hideWhenNotSignedIn;
    }
    const hideNotDOAADmin = hideWhenNotDOAAdmin ?? true;
    const hideIsDOAAdmin = isDOAAdmin && (!hideNotDOAADmin || text === 'Declaration of Availability');
    const hideFABoard = name === TelephoneBoards["First Aid"] && !onFABoard;
    const hideSQCBoard = name === TelephoneBoards.Squamish && !onSQCBoard;
    const hideDCBoard = name === TelephoneBoards["Dock Checkers"] && !onDCBoard;
    const hideBoard = hideFABoard || hideSQCBoard || hideDCBoard;
    const hideNonVancouverUser = hideWhenNonVancouverUser && !board;
    // Check if children exist and filter them recursively
    if (children && children.length > 0) {
      item.children = filterNavigation(children, smbAuthenticated, isDOAAdmin, onFABoard, onSQCBoard, onDCBoard, board);
    }
    return !(
      hideIsDOAAdmin ||
      (!isDOAAdmin && hideWhenNotDOAAdmin) ||
      (isTelephone ? (onTB && text === "Telephone Boards") : hideWhenNotTelephone) ||
      hideBoard ||
      hideNonVancouverUser ||
      (!isLinesComplementBoard && hideWhenNotLines && !isLines)
    );
  };

  const filteredNavigation = filterNavigation(
    navigation,
    smbAuthenticated,
    isDOAAdmin,
    onFABoard,
    onSQCBoard,
    onDCBoard,
    board
  );

  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { style, containerRef } = useStickyBottom();

  useEffect(() => {
    setExpandedItems(navigation.reduce((accumulator: string[], item) => {
      if (item.expandByDefault) {
        accumulator.push(item.text);
      }
      return accumulator;
    }, []))
  }, []);

  const handleOnClick = (item: INavDrawerItem) => {
    if (item.children && item.children.length) {
      const newExpandedItems = expandedItems.includes(item.text) ?
        expandedItems.filter(expandedtem => expandedtem !== item.text) : [...expandedItems, item.text];

      setExpandedItems(newExpandedItems);
    } else {
      onNavTo(item.nav);
      onClose();
    }
  };

  const handleOnClickContactUs = () => {
    navigate(`/contact-us${signedIn ? `/signedIn` : ``}`);
    onClose();
  }

  return (
    <Drawer anchor={"left"} open={open} onClose={onClose}>
      <Box sx={MenuBoxStyle} data-automation="smbMenuFull" role="presentation">
        <List
          className="nav-menu"
          ref={containerRef}
          sx={MenuStyle}
          component="nav"
          aria-labelledby="nested-list-subheader"
          subheader={
            <ListSubheader sx={MenuSubHeaderStyle} component="div" id="nested-list-subheader">
              <ListItem className="header" disablePadding>
                <ListItemButton
                  sx={MenuSubHeaderItemButtonStyle}
                  onClick={handleOnClickContactUs}
                  className={nav.includes("contact-us") ? "selected" : ""}
                  selected={nav.includes('contact-us')}>
                  <ListItemIcon><ContactMailRounded /></ListItemIcon>
                  <ListItemText primary={<Typography data-automation="contact-us nav-menu-item nav-btn">
                    Contact Us
                  </Typography>} />
                </ListItemButton>
                {signedIn &&
                  <Typography className="topListItem" data-automation="last-login">
                    Your last login: {lastLoginTime}
                  </Typography>
                }
              </ListItem>
            </ListSubheader>
          }
        >
          {filteredNavigation.map((item: INavDrawerItem) => {
            const disabled = item.disableWhenNotSignedIn && !smbAuthenticated;
            const selected = nav === item.nav || !!item.relatedRotes?.find(rr => nav.includes(rr));
            return (
              <React.Fragment key={item.text}>
                <ListItem divider sx={MenuListItemStyle} disablePadding>
                  <ListItemButton
                    disabled={disabled}
                    onClick={() => handleOnClick(item)}
                    className={selected ? "selected" : ""}
                    selected={selected}>
                    {item.icon ? <ListItemIcon>{item.icon}</ListItemIcon> : null}
                    <ListItemText
                      primary={
                        <Typography
                          data-automation={`nav-menu-item ${item.children && item.children.length ? 'category-btn' : 'nav-btn'}`}
                        >
                          {item.text}
                        </Typography>
                      }
                    />
                    {item.children && item.children.length ? <>
                      {expandedItems.includes(item.text) ?
                        <KeyboardDoubleArrowUpRounded className={disabled ? 'disabled' : ''} /> :
                        <KeyboardDoubleArrowDownRounded className={disabled ? 'disabled' : ''} />
                      }
                    </> : null}
                  </ListItemButton>
                </ListItem>
                {item.children && item.children.length ? (
                  <Collapse sx={{ background: 'white' }} in={expandedItems.includes(item.text)} timeout="auto" unmountOnExit>
                    <List style={{ marginLeft: '56px' }} disablePadding>
                      {
                        item.children.map((child) => {
                          const disabledChild = (child.disableWhenNotSignedIn && !smbAuthenticated);
                          const subNav = nav.split('/').filter(Boolean)[0];
                          const navTitle = subNav && COMPLEX_NAV_TITLES.includes(subNav) ? `/${subNav}` : nav;

                          const selected = navTitle.includes(child.nav) || !!child.relatedRotes
                            ?.find(rr => navTitle.includes(rr));

                          return (
                            <ListItem className="nav-menu" divider sx={MenuListItemStyle} key={child.text} disablePadding>
                              <ListItemButton
                                disabled={disabledChild}
                                onClick={() => handleOnClick(child)}
                                className={selected ? "selected" : ""}
                                selected={selected}>
                                {child.icon ? <ListItemIcon>{child.icon}</ListItemIcon> : null}
                                <ListItemText
                                  primary={
                                    <Typography data-automation="nav-menu-item nav-btn">{child.text}</Typography>
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          )
                        })
                      }
                    </List>
                  </Collapse>
                ) : null}
              </React.Fragment>
            )
          })}
          {signedIn &&
            <ListItem disablePadding sx={(theme: ITheme) => ({ ...style, ...MenuSubFooterStyle(theme) })}>
              <ListItemButton onClick={onSignOut}>
                <ListItemIcon><ExitToApp /></ListItemIcon>
                <ListItemText primary={<Typography data-automation="sign-out nav-menu-item nav-btn">Sign Out</Typography>} />
              </ListItemButton>
            </ListItem>
          }
        </List>
      </Box>
    </Drawer>)
};